// PlanYear detail — single Overview page.
//
// PlanYear is an operating record: the page no longer hosts its own
// Documents or Commitments tabs. Documents are reached through
// Account Documents (deep-linked to ``/plan-years/{year-range}/``);
// commitments are filtered through the canonical Commitment Tracker.
//
// Claimed sections cover Plan Design Summary, Renewal Timeline, and
// Activity. They render coming-soon placards now and absorb real
// data when each feature ships.

import { ArrowLeft, ExternalLink } from "lucide-react";
import { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import { BlockNoteEditor } from "@/components/blocknote";
import { Table, THead, TBody, Tr, Th, Td } from "@/design-system";
import { useOptionalCommitmentModal } from "@/features/commitments";

import { formatPlanYearRange } from "./dateFormat";
import { useAccountDetailQuery } from "./hooks";
import {
  PLAN_YEARS_QUERY_KEY,
  useArchivePlanYearMutation,
  usePlanYearDetailQuery,
  useRestorePlanYearMutation,
  useUpdatePlanYearMutation,
} from "./planYearHooks";
import type { PlanYearRow } from "./planYearApi";


export function PlanYearDetailPage() {
  const params = useParams();
  const accountId = params.accountId ?? "";
  const planYearId = params.planYearId ?? "";

  const accountQuery = useAccountDetailQuery(accountId);
  const planYearQuery = usePlanYearDetailQuery(accountId, planYearId);

  if (planYearQuery.isLoading) {
    return (
      <div data-testid="plan-year-detail-page" className="flex flex-col gap-5">
        <p className="text-sm italic text-text-tertiary">Loading…</p>
      </div>
    );
  }
  if (planYearQuery.isError || !planYearQuery.data) {
    return (
      <div data-testid="plan-year-detail-page" className="flex flex-col gap-5">
        <p
          role="alert"
          className="rounded-md bg-ember-tint px-3 py-2 text-sm text-ember-ink"
        >
          Couldn't load this plan year.
        </p>
        <Link
          to={`/dashboard/accounts/${accountId}`}
          className="text-xs font-medium text-text-secondary hover:text-text-primary"
        >
          Back to Account
        </Link>
      </div>
    );
  }

  const planYear = planYearQuery.data;
  const accountName = accountQuery.data?.legal_name ?? "Account";
  const yearRange =
    (planYear as PlanYearRow & { year_range?: string }).year_range
    ?? `${planYear.effective_start.slice(0, 4)}-${planYear.effective_end.slice(0, 4)}`;
  const docsFolder =
    (planYear as PlanYearRow & { documents_folder_path?: string })
      .documents_folder_path ?? `/plan-years/${yearRange}/`;
  const docsHref =
    `/dashboard/accounts/${encodeURIComponent(accountId)}`
    + `?tab=documents&folder=${encodeURIComponent(docsFolder)}`;
  const commitmentsHref =
    `/dashboard/commitment-tracker?account_id=${encodeURIComponent(accountId)}`
    + `&plan_year_id=${encodeURIComponent(planYearId)}`;

  return (
    <div
      data-testid="plan-year-detail-page"
      className="flex flex-col gap-5"
    >
      <Header
        accountId={accountId}
        accountName={accountName}
        planYear={planYear}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <CoveragePeriodCard planYear={planYear} accountId={accountId} />
        <OpenEnrollmentCard planYear={planYear} accountId={accountId} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <WorkSummaryCard
          planYear={planYear}
          commitmentsHref={commitmentsHref}
          accountId={accountId}
        />
        <DocumentsShortcutCard
          docsHref={docsHref}
          folder={docsFolder}
        />
      </div>

      <PlanDesignSummarySection />

      {/* Notes anchor at the bottom — keep the operating summary
          (dates, work, documents shortcut, plan design) above the
          fold; long-form context stays available below. */}
      <NotesCard planYear={planYear} accountId={accountId} />
    </div>
  );
}


// ── Header ────────────────────────────────────────────────────────────────


function Header({
  accountId, accountName, planYear,
}: {
  accountId: string;
  accountName: string;
  planYear: PlanYearRow;
}) {
  const archiveMutation = useArchivePlanYearMutation(accountId);
  const restoreMutation = useRestorePlanYearMutation(accountId);
  return (
    <header className="flex flex-col gap-2">
      <Link
        to={`/dashboard/accounts/${accountId}`}
        data-testid="plan-year-back"
        className="inline-flex w-fit items-center gap-1 text-xs font-medium text-text-secondary hover:text-text-primary"
      >
        <ArrowLeft size={12} aria-hidden="true" />
        Back to {accountName}
      </Link>
      <div className="flex items-center gap-3">
        <h1
          data-testid="plan-year-title"
          className="text-lg font-semibold text-text-primary"
        >
          Plan year {formatRange(planYear)}
        </h1>
        {planYear.is_current && (
          <span
            data-testid="plan-year-current-badge"
            className="rounded-full bg-sage-tint px-2 py-0.5 text-[11px] font-medium text-teal"
          >
            Current
          </span>
        )}
        {planYear.is_archived && (
          <span
            data-testid="plan-year-archived-badge"
            className="rounded-full bg-recessed px-2 py-0.5 text-[11px] text-text-secondary"
          >
            Archived
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {planYear.is_archived ? (
            <button
              type="button"
              data-testid="plan-year-detail-restore"
              disabled={restoreMutation.isPending}
              onClick={() => restoreMutation.mutate(planYear.plan_year_id)}
              className="rounded-md border border-border-subtle px-2 py-1 text-xs"
            >
              {restoreMutation.isPending ? "Restoring…" : "Restore"}
            </button>
          ) : (
            <button
              type="button"
              data-testid="plan-year-detail-archive"
              disabled={archiveMutation.isPending}
              onClick={() => archiveMutation.mutate(planYear.plan_year_id)}
              className="rounded-md border border-border-subtle px-2 py-1 text-xs"
            >
              {archiveMutation.isPending ? "Archiving…" : "Archive"}
            </button>
          )}
        </span>
      </div>
    </header>
  );
}


// ── Cards ─────────────────────────────────────────────────────────────────


function CoveragePeriodCard({
  planYear, accountId,
}: {
  planYear: PlanYearRow;
  accountId: string;
}) {
  const updateMutation = useUpdatePlanYearMutation(accountId);
  return (
    <Card title="Coverage period">
      <DateField
        label="Effective start"
        value={planYear.effective_start}
        onSave={(v) =>
          updateMutation.mutateAsync({
            planYearId: planYear.plan_year_id,
            values: { effective_start: v },
          })
        }
      />
      <DateField
        label="Effective end"
        value={planYear.effective_end}
        onSave={(v) =>
          updateMutation.mutateAsync({
            planYearId: planYear.plan_year_id,
            values: { effective_end: v },
          })
        }
      />
      {/* "Current" is now derived from today's date — the BE flips
          ``is_current`` on the wire based on whether today falls
          within the effective range. The old "Mark as current"
          checkbox is gone; surface the derived state read-only so
          users can still see the plan year's status. */}
      <p
        data-testid="plan-year-overview-current-status"
        className="text-xs text-text-secondary"
      >
        {planYear.is_current
          ? "This plan year is current — today's date falls within its coverage period."
          : "Not the current plan year — today's date is outside its coverage period."}
      </p>
    </Card>
  );
}


function OpenEnrollmentCard({
  planYear, accountId,
}: {
  planYear: PlanYearRow;
  accountId: string;
}) {
  const updateMutation = useUpdatePlanYearMutation(accountId);
  return (
    <Card title="Open enrollment">
      <DateField
        label="Start"
        value={planYear.open_enrollment_start}
        optional
        onSave={(v) =>
          updateMutation.mutateAsync({
            planYearId: planYear.plan_year_id,
            values: { open_enrollment_start: v || null },
          })
        }
      />
      <DateField
        label="End"
        value={planYear.open_enrollment_end}
        optional
        onSave={(v) =>
          updateMutation.mutateAsync({
            planYearId: planYear.plan_year_id,
            values: { open_enrollment_end: v || null },
          })
        }
      />
    </Card>
  );
}


function NotesCard({
  planYear, accountId,
}: {
  planYear: PlanYearRow;
  accountId: string;
}) {
  const updateMutation = useUpdatePlanYearMutation(accountId);
  const queryClient = useQueryClient();
  return (
    <Card title="Notes">
      <BlockNoteEditor
        documentId={planYear.notes_document_id ?? null}
        documentKind="plan_year_notes"
        fallbackText={planYear.notes ?? null}
        onCreated={async (documentId) => {
          await updateMutation.mutateAsync({
            planYearId: planYear.plan_year_id,
            values: { notes_document_id: documentId },
          });
          await queryClient.invalidateQueries({
            queryKey: [
              PLAN_YEARS_QUERY_KEY, "detail", accountId,
              planYear.plan_year_id,
            ],
          });
        }}
      />
    </Card>
  );
}


function WorkSummaryCard({
  planYear, commitmentsHref, accountId,
}: {
  planYear: PlanYearRow;
  commitmentsHref: string;
  accountId: string;
}) {
  const closePreview = useClosePreview(accountId, planYear.plan_year_id);
  const commitmentModal = useOptionalCommitmentModal();
  const location = useLocation();
  return (
    <Card
      title="Work summary"
      // Counts here are scoped to commitments tied to this specific
      // plan year via ``plan_year_id``. Account-level commitments
      // without a plan year don't roll up here — surfacing the plan
      // year range under the title makes that scope obvious so the
      // numbers don't look out of sync with the broader tracker.
      subtitle={
        <span data-testid="plan-year-work-summary-scope">
          For {formatRange(planYear)} — Commitments without a plan year
          aren't counted.
        </span>
      }
    >
      <ul
        data-testid="plan-year-work-summary"
        className="grid grid-cols-3 gap-2 text-xs"
      >
        <Stat label="Completed" value={planYear.done_commitments} />
        <Stat label="Incomplete" value={planYear.open_commitments} />
        <Stat label="Archived" value={planYear.archived_commitments} />
      </ul>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {commitmentModal ? (
          <button
            type="button"
            data-testid="plan-year-new-commitment"
            onClick={() =>
              commitmentModal.openCreateCommitmentModal({
                defaults: {
                  account_id: accountId,
                  plan_year_id: planYear.plan_year_id,
                },
                returnContext: {
                  fromUrl: `${location.pathname}${location.search}`,
                  fromLabel: "Back to Plan Year",
                },
              })
            }
            className="inline-flex items-center gap-1 rounded-md bg-teal px-3 py-1.5 text-xs font-medium text-text-inverse hover:opacity-90"
          >
            + New Commitment
          </button>
        ) : (
          <Link
            to={
              `/dashboard/commitments/new?account_id=${encodeURIComponent(accountId)}`
              + `&plan_year_id=${encodeURIComponent(planYear.plan_year_id)}`
            }
            data-testid="plan-year-new-commitment"
            className="inline-flex items-center gap-1 rounded-md bg-teal px-3 py-1.5 text-xs font-medium text-text-inverse hover:opacity-90"
          >
            + New Commitment
          </Link>
        )}
        <Link
          to={commitmentsHref}
          data-testid="plan-year-view-commitments"
          className="inline-flex items-center gap-1 rounded-md border border-border-subtle px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary"
        >
          View Plan Year Commitments
        </Link>
        <button
          type="button"
          data-testid="plan-year-archive-completed"
          onClick={() => void closePreview.run()}
          disabled={closePreview.busy}
          className="inline-flex items-center gap-1 rounded-md border border-border-subtle px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary"
        >
          Archive completed work
        </button>
      </div>
      {closePreview.modal}
    </Card>
  );
}


function DocumentsShortcutCard({
  docsHref, folder,
}: { docsHref: string; folder: string }) {
  return (
    <Card title="Plan year documents">
      <p className="text-xs text-text-secondary">
        Documents live inside Account Documents. Open the
        <code className="mx-1 rounded bg-recessed px-1 text-[11px]">
          {folder}
        </code>
        folder to upload, browse, and link plan-year files.
      </p>
      <Link
        to={docsHref}
        data-testid="plan-year-open-documents"
        className="mt-2 inline-flex w-fit items-center gap-1 rounded-md bg-teal px-3 py-1.5 text-xs font-medium text-text-inverse hover:opacity-90"
      >
        <ExternalLink size={12} aria-hidden="true" />
        Open plan-year folder
      </Link>
    </Card>
  );
}


function Stat({ label, value }: { label: string; value: number }) {
  return (
    <li className="rounded-md bg-recessed/40 px-3 py-2">
      <span className="block text-[10px] uppercase tracking-wide text-text-tertiary">
        {label}
      </span>
      <span className="block text-base font-semibold text-text-primary">
        {value}
      </span>
    </li>
  );
}


// ── Claimed coming-soon spaces ────────────────────────────────────────────


function PlanDesignSummarySection() {
  return (
    <section
      data-testid="plan-year-plan-design-summary"
      className="flex flex-col gap-3 rounded-md border border-border-subtle bg-canvas/40 p-4"
    >
      <header className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-text-primary">
          Plan design summary
        </h2>
        <span className="rounded-full bg-recessed px-2 py-0.5 text-[10px] uppercase tracking-wide text-text-tertiary">
          Coming soon
        </span>
      </header>
      <p className="text-xs text-text-secondary">
        Structured plan data extracted from SBCs, renewal proposals,
        rate sheets, and carrier documents will appear here.
      </p>
      <Table
        density="xs"
        wrapperClassName="rounded-md border border-border-subtle"
      >
        <THead className="bg-recessed/40 text-text-tertiary">
          <Tr>
            {[
              "Coverage", "Carrier", "Plan", "Type",
              "Funding", "Network", "Status", "Source",
            ].map((h) => (
              <Th key={h}>{h}</Th>
            ))}
          </Tr>
        </THead>
        <TBody>
          <Tr>
            <Td
              colSpan={8}
              className="py-6 text-center italic text-text-tertiary"
            >
              No plan-design records yet.
            </Td>
          </Tr>
        </TBody>
      </Table>
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="plan-year-add-plan-manual"
          disabled
          className="rounded-md border border-border-subtle px-3 py-1.5 text-xs text-text-tertiary"
        >
          Add plan manually
        </button>
        <button
          type="button"
          data-testid="plan-year-extract-plan"
          disabled
          className="rounded-md border border-border-subtle px-3 py-1.5 text-xs text-text-tertiary"
        >
          Extract from documents
        </button>
      </div>
    </section>
  );
}


// ── Shared bits ───────────────────────────────────────────────────────────


function DateField({
  label, value, optional, onSave,
}: {
  label: string;
  value: string | null;
  optional?: boolean;
  onSave: (next: string) => Promise<unknown>;
}) {
  const [local, setLocal] = useState(value ?? "");
  return (
    <label className="flex flex-col gap-1 text-xs text-text-secondary">
      {label}{optional && " (optional)"}
      <input
        type="date"
        value={local ?? ""}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          if (local !== (value ?? "")) {
            void onSave(local);
          }
        }}
        className="rounded-md border border-border-subtle bg-raised px-2 py-1 text-sm text-text-primary"
      />
    </label>
  );
}


function Card({
  title, subtitle, children,
}: {
  title: string;
  /** Optional caption rendered below the title — used to disambiguate
   * scope (e.g. "Counts only Commitments in this plan year"). */
  subtitle?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2 rounded-md border border-border-subtle bg-canvas/40 p-4">
      <header className="flex flex-col gap-0.5">
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
        {subtitle && (
          <p className="text-[11px] text-text-tertiary">{subtitle}</p>
        )}
      </header>
      {children}
    </section>
  );
}


// ── Close preview / archive completed work ───────────────────────────────


interface ClosePreviewState {
  archived_count: number;
  incomplete_count: number;
}


function useClosePreview(accountId: string, planYearId: string) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<ClosePreviewState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  async function run() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<ClosePreviewState>(
        `/api/v1/accounts/${encodeURIComponent(accountId)}`
        + `/plan_years/${encodeURIComponent(planYearId)}/close`,
        { dry_run: true },
      );
      setPreview(res);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Couldn't preview close.");
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<ClosePreviewState>(
        `/api/v1/accounts/${encodeURIComponent(accountId)}`
        + `/plan_years/${encodeURIComponent(planYearId)}/close`,
        { dry_run: false },
      );
      setPreview(null);
      void queryClient.invalidateQueries({
        queryKey: [PLAN_YEARS_QUERY_KEY, "detail", accountId, planYearId],
      });
      void queryClient.invalidateQueries({
        queryKey: [PLAN_YEARS_QUERY_KEY, "list"],
      });
      return res;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Couldn't archive.");
    } finally {
      setBusy(false);
    }
  }

  const modal = preview ? (
    <div
      data-testid="plan-year-close-preview"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-canvas/80"
    >
      <div className="flex w-[440px] flex-col gap-3 rounded-md border border-border-subtle bg-raised p-4 shadow-lg">
        <h3 className="text-sm font-semibold text-text-primary">
          Archive completed Commitments?
        </h3>
        <p className="text-sm text-text-secondary">
          Will archive <strong>{preview.archived_count}</strong>{" "}
          completed Commitment
          {preview.archived_count === 1 ? "" : "s"}.
        </p>
        {preview.incomplete_count > 0 && (
          <p
            data-testid="plan-year-close-warning"
            className="rounded-md bg-warning-tint px-2 py-1 text-xs text-warning"
          >
            {preview.incomplete_count} incomplete Commitment
            {preview.incomplete_count === 1 ? " is" : "s are"} still
            active and will be left in place.
          </p>
        )}
        {error && (
          <p role="alert" className="text-xs text-ember-ink">{error}</p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="plan-year-close-cancel"
            disabled={busy}
            onClick={() => setPreview(null)}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-xs text-text-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="plan-year-close-confirm"
            disabled={busy}
            onClick={() => void confirm()}
            className="rounded-md bg-teal px-3 py-1.5 text-xs font-medium text-text-inverse hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Archiving…" : "Archive completed"}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { busy, run, modal };
}


// Plan-year date helpers re-exported from ``./dateFormat`` so every
// surface renders YYYY-MM-DD strings TZ-safely.
function formatRange(planYear: PlanYearRow): string {
  return formatPlanYearRange(
    planYear.effective_start, planYear.effective_end,
  );
}
